/**
 * Bringing a board cell into view.
 *
 * Not `scrollIntoView`. Three things about this board defeat it:
 *
 *   - The scrollport is a different element in every view (.board-scroll on the day board,
 *     .board-wrap on the week and fortnight), and .board-page sits above all of them
 *     accepting scroll writes that do nothing. So the scroller is FOUND, by walking up from
 *     the cell to the first ancestor that actually scrolls, rather than named.
 *   - The column headers and the staff column are sticky. `block: 'nearest'` computes a zero
 *     delta for a cell already technically inside the scrollport and leaves it sitting under
 *     the header, which is the one case where scrolling was the whole point.
 *   - The notes drawer is a 420px panel over the right-hand edge, so the usable width is
 *     narrower than the scrollport while it is open.
 *
 * Hence the offsets are computed here against the scrollport's own rect, with the sticky
 * furniture measured rather than assumed.
 */

/** The nearest ancestor that genuinely scrolls on this axis — not merely `overflow: auto`. */
function scrollerFor(el, axis) {
  const sizeProp = axis === 'y' ? 'scrollHeight' : 'scrollWidth'
  const clientProp = axis === 'y' ? 'clientHeight' : 'clientWidth'
  const overflowProp = axis === 'y' ? 'overflowY' : 'overflowX'
  for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
    const cs = getComputedStyle(n)
    const scrolls = ['auto', 'scroll'].includes(cs[overflowProp])
    if (scrolls && n[sizeProp] > n[clientProp] + 1) return n
  }
  return null
}

/** How far the sticky header reaches down into the scrollport, measured from the live DOM. */
function stickyTopInset(scroller) {
  const head = scroller.querySelector('thead')
  if (!head) return 0
  const rows = [...head.querySelectorAll('tr')]
  return rows.reduce((h, r) => h + r.getBoundingClientRect().height, 0)
}

/** And how far the pinned staff column reaches in from the left. */
function stickyLeftInset(scroller, cell) {
  const row = cell.closest('tr')
  const first = row?.querySelector('td.staff-cell, th.col-staff')
  if (!first || first === cell) return 0
  return getComputedStyle(first).position === 'sticky'
    ? first.getBoundingClientRect().width
    : 0
}

/**
 * Scroll `cell` into clear view.
 *
 * `blockedRight` is width taken out of the scrollport by something floating over it — the
 * notes drawer, while it is still open. Scrolling a cell to a position the drawer covers is
 * the same as not scrolling it.
 *
 * Returns true if it scrolled anything, false if the cell was already clear (which is a
 * perfectly good outcome, not a failure).
 */
export function revealCell(cell, { blockedRight = 0, pad = 12 } = {}) {
  if (!cell || !cell.isConnected) return false
  let moved = false

  const vScroller = scrollerFor(cell, 'y')
  if (vScroller) {
    const port = vScroller.getBoundingClientRect()
    const box = cell.getBoundingClientRect()
    const top = port.top + stickyTopInset(vScroller) + pad
    const bottom = port.bottom - pad
    if (box.top < top) {
      vScroller.scrollTop -= top - box.top
      moved = true
    } else if (box.bottom > bottom) {
      // Never so far that the top of the cell disappears under the header — a tall cell is
      // better shown from its top than scrolled past it.
      vScroller.scrollTop += Math.min(box.bottom - bottom, Math.max(0, box.top - top))
      moved = true
    }
  }

  const hScroller = scrollerFor(cell, 'x')
  if (hScroller) {
    const port = hScroller.getBoundingClientRect()
    const box = cell.getBoundingClientRect()
    const left = port.left + stickyLeftInset(hScroller, cell) + pad
    const right = port.right - blockedRight - pad
    if (box.left < left) {
      hScroller.scrollLeft -= left - box.left
      moved = true
    } else if (box.right > right) {
      hScroller.scrollLeft += Math.min(box.right - right, Math.max(0, box.left - left))
      moved = true
    }
  }

  return moved
}
